import { type JSX } from 'react';
import {
  Navigate,
  Route,
  Routes,
  useParams,
  useSearchParams,
} from 'react-router-dom';
import { CatalogId } from '@kirin/api-client/contracts/ai-instruction-catalog';
import { PoliciesList } from '@/components/policylist/PoliciesList';
import { AiInstructionCatalogPage } from '@/components/AiInstructionCatalog/AiInstructionCatalogPage';
import { BuiltInCatalogPage } from '@/components/policylist/components/BuiltInCatalogPage';
import { CustomCapabilityRulesPage } from '@/components/policylist/components/CustomCapabilityRulesPage';
import { GitleaksRulesPage } from '@/components/policylist/components/GitleaksRulesPage';
import { useFeatureFlags } from '@/hooks/useFeatureFlags';
import { flagsmithFeatureFlags } from '@/lib/flagsmith';
import {
  useOrganizationPolicies,
  useToggleOrganizationPolicy,
  useUpdateOrganizationPolicySettings,
  useResetOrganizationPolicySettings,
  useOrganizationPolicySettings,
} from '@/hooks/api/useOrganizationPolicies';
import {
  useGroupPolicies,
  useGroupPolicySettings,
  useResetGroupPolicySettings,
  useToggleGroupPolicy,
  useUpdateGroupPolicySettings,
} from '@/hooks/api/useGroupPolicies';
import {
  useMySecurityGroup,
  useSecurityGroups,
} from '@/hooks/api/useSecurityGroups';
import { useRoleCheck } from '@/hooks/useRoleCheck';
import { useScopeChangeAction } from '@/hooks/useScopeChangeHooks';
import { OrganizationRole, roleGroups } from '@/lib/roles';
import {
  AI_AGENT_INSTRUCTIONS_SLUG,
  EXTENSION_SECURITY_CATALOG_SLUG,
  EXTENSION_SECURITY_CUSTOM_RULES_SLUG,
  SECRET_DETECTION_GITLEAKS_RULES_SLUG,
  POLICIES_AI_INSTRUCTIONS_SLUG,
  POLICIES_ALLOW_BLOCK_SLUG,
  POLICY_SCOPE_PARAM,
} from '@/lib/policyPaths';
import { Button } from '@/components/ui/button';
import { MemberScopeNotice } from '../components/MemberScopeNotice';
import { PolicyScopeSelector } from '../components/PolicyScopeSelector';
import type { PolicySection } from '@/components/policylist/constants';

export default function PoliciesTab() {
  const { organizationId } = useParams<{ organizationId: string }>();

  const { hasAccess, isLoading: isRoleCheckLoading } = useRoleCheck({
    param: 'organizationId',
    anyOf: [OrganizationRole.Owner, OrganizationRole.Manager],
  });

  // Gate the Manage-catalog subroute behind the feature flag so a direct
  // deep-link doesn't render a page whose APIs the backend has disabled.
  const { flags, isLoading: isFlagLoading } = useFeatureFlags([
    flagsmithFeatureFlags.AI_INSTRUCTION_CATALOG,
    flagsmithFeatureFlags.CAPABILITY_DETECTION,
    flagsmithFeatureFlags.SECURITY_GROUPS_POLICIES,
    flagsmithFeatureFlags.GITLEAKS_RULE_MANAGEMENT,
  ]);
  // Also gates the "AI Agent Instructions" sub-page (KIR-3982): the backend
  // registers that policy group behind this same flag, so a deep-link can't
  // land on a page whose group the API doesn't return.
  const catalogEnabled =
    flags[flagsmithFeatureFlags.AI_INSTRUCTION_CATALOG] ?? false;
  const capabilityDetectionEnabled =
    flags[flagsmithFeatureFlags.CAPABILITY_DETECTION] ?? false;
  const gitleaksRuleManagementEnabled =
    flags[flagsmithFeatureFlags.GITLEAKS_RULE_MANAGEMENT] ?? false;
  // KIR-3800: with the flag off the backend disables the group APIs, so the
  // scope selector is hidden and no group request is ever issued.
  const securityGroupsEnabled =
    flags[flagsmithFeatureFlags.SECURITY_GROUPS_POLICIES] ?? false;

  // KIR-3800 scope selection. The scope lives in a query param so every existing
  // policies sub-route keeps working and a scoped view stays linkable.
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedGroupId = searchParams.get(POLICY_SCOPE_PARAM);
  // The groups endpoint is manager/owner/support but the Policies tab is
  // anyRole, so gating on the flag alone made every member 403 on each page
  // load. The selector is admin-only anyway — a member cannot edit policies.
  const {
    hasAccess: canUseSecurityGroups,
    isLoading: isSecurityGroupsRoleLoading,
  } = useRoleCheck({
    param: 'organizationId',
    anyOf: [...roleGroups.managerOwnerOrSupport],
  });
  const { data: securityGroups, isLoading: isLoadingSecurityGroups } =
    useSecurityGroups(
      organizationId,
      securityGroupsEnabled && canUseSecurityGroups
    );
  /*
    KIR-4334. A member can use neither the groups list nor the scope selector, so
    they always landed on the ORGANIZATION policies even when a group's overrides
    were the ones enforced against them. Ask the member-readable endpoint which
    group they are in and default the scope to it.

    `?scope=` is ignored here: a member cannot switch scope, so a hand-edited
    param must not select someone else's group (the backend refuses it anyway).

    `isSecurityGroupsRoleLoading` is in the condition because `useRoleCheck`
    answers `hasAccess: false` while the Descope user loads — without it an admin
    who also belongs to a group would be scoped to it for that window.
  */
  const isMemberOnly =
    securityGroupsEnabled &&
    !isSecurityGroupsRoleLoading &&
    !canUseSecurityGroups;
  const {
    data: myGroup,
    isLoading: isLoadingMyGroup,
    // `isLoading` is only true while `fetchStatus === 'fetching'`. A paused
    // fetch (browser offline, before `networkMode: 'always'`) is `isPending`
    // with both `isLoading` and `isError` false — the same shape as "no group".
    isPending: isMyGroupPending,
    isError: isMyGroupError,
    refetch: refetchMyGroup,
  } = useMySecurityGroup(organizationId, isMemberOnly);
  const memberGroup = isMemberOnly ? (myGroup?.group ?? null) : null;

  // Only honour a scope that names a group that still exists. A deleted group
  // (or a hand-edited URL) falls back to the organization scope rather than
  // rendering a page whose every request 404s.
  const activeGroupId = isMemberOnly
    ? (memberGroup?.id ?? null)
    : requestedGroupId &&
        securityGroups?.some(group => group.id === requestedGroupId)
      ? requestedGroupId
      : null;
  const isGroupScope = activeGroupId !== null;

  /*
    A deep link names a scope we cannot validate until the groups list arrives.
    Until then `activeGroupId` is null, so the page would mount the ORGANIZATION
    hooks — with live, enabled edit controls and no scope selector on screen yet
    (it renders nothing while loading). A toggle clicked in that window would
    silently write to the organization while the user believes they are editing
    a group.

    So when a scope is requested and the list is still loading, render nothing
    until it resolves. Only requests that actually carry a `?scope=` pay this;
    the plain organization view is unaffected.

    KIR-4334 adds the member path, same hazard reached differently: its scope
    comes from the flag, the role check and `/security-groups/me`, and until all
    three answer we would mount the ORGANIZATION hooks and flash the org policies
    — the very thing this page is being fixed to stop showing.
  */
  const isResolvingScope = Boolean(
    (requestedGroupId && securityGroupsEnabled && isLoadingSecurityGroups) ||
    isFlagLoading ||
    isSecurityGroupsRoleLoading ||
    (isMemberOnly && (isLoadingMyGroup || isMyGroupPending))
  );

  /*
    The URL still names a group, the list HAS loaded, and that group is not in
    it — it was deleted (or the caller invented an id). We fall back to the
    organization scope, which is safe, but silently: the page would look
    identical to a normal org visit while the URL implies otherwise, and the
    next toggle would write org-wide. Say so instead.
  */
  const requestedScopeUnavailable = Boolean(
    requestedGroupId &&
    securityGroupsEnabled &&
    canUseSecurityGroups &&
    !isLoadingSecurityGroups &&
    securityGroups &&
    !activeGroupId
  );

  /*
    KIR-4334. The member's own group could not be resolved — the hook resolves the
    flag-off 404 itself, so this is a 403 or a fault. Falling back to the
    organization scope here is exactly the bug this page was fixed for: the member
    would be shown the default policies as if they were the ones assigned to them,
    with nothing on screen saying otherwise. So render no policy list at all.
  */
  const isMemberScopeUnresolved = isMemberOnly && isMyGroupError;

  const { changeScopeTo } = useScopeChangeAction();

  // Switching scope routes through the shared scope-change guard, which shows
  // the unsaved-changes dialog when any policy form on the page is dirty and
  // otherwise applies the change immediately (AC #9).
  const handleScopeChange = (groupId: string | null): void => {
    changeScopeTo({
      id: `policy-scope:${groupId ?? 'organization'}`,
      title: 'Discard unsaved policy changes?',
      body: 'You have unsaved changes in this scope. Switching scope discards them.',
      discardLabel: 'Discard and switch',
      run: () => {
        setSearchParams(
          current => {
            const next = new URLSearchParams(current);
            if (groupId) {
              next.set(POLICY_SCOPE_PARAM, groupId);
            } else {
              next.delete(POLICY_SCOPE_PARAM);
            }
            return next;
          },
          { replace: true }
        );
      },
    });
  };

  // Both hook families are always called so hook order stays stable; `enabled`
  // makes only the selected scope actually fetch.
  const orgPoliciesQuery = useOrganizationPolicies(
    organizationId,
    !isGroupScope
  );
  const groupPoliciesQuery = useGroupPolicies(
    organizationId,
    activeGroupId ?? undefined,
    isGroupScope
  );
  const {
    data: policies,
    isLoading,
    error,
  } = isGroupScope ? groupPoliciesQuery : orgPoliciesQuery;

  const orgToggleMutation = useToggleOrganizationPolicy(organizationId);
  const groupToggleMutation = useToggleGroupPolicy(
    organizationId,
    activeGroupId ?? undefined
  );
  const togglePolicyMutation = isGroupScope
    ? groupToggleMutation
    : orgToggleMutation;

  const orgUpdateMutation = useUpdateOrganizationPolicySettings(organizationId);
  const groupUpdateMutation = useUpdateGroupPolicySettings(
    organizationId,
    activeGroupId ?? undefined
  );
  const updateSettingsMutation = isGroupScope
    ? groupUpdateMutation
    : orgUpdateMutation;

  const orgResetMutation = useResetOrganizationPolicySettings(organizationId);
  const groupResetMutation = useResetGroupPolicySettings(
    organizationId,
    activeGroupId ?? undefined
  );
  const resetSettingsMutation = isGroupScope
    ? groupResetMutation
    : orgResetMutation;

  const usePolicySettings = (policyKey: string | undefined, enabled = true) => {
    const orgSettings = useOrganizationPolicySettings(
      organizationId,
      policyKey,
      enabled && !isGroupScope
    );
    const groupSettings = useGroupPolicySettings(
      organizationId,
      activeGroupId ?? undefined,
      policyKey,
      enabled && isGroupScope
    );
    return isGroupScope ? groupSettings : orgSettings;
  };

  const errorMessage = error
    ? error instanceof Error
      ? error.message
      : String(error)
    : undefined;

  if (!organizationId) {
    return null;
  }

  // See `isResolvingScope`: never render editable policies before we
  // know which scope a `?scope=` deep link resolves to.
  if (isResolvingScope) {
    return <p className="text-sm text-muted-foreground">Loading policies...</p>;
  }

  // Never silently substitute the organization's policies for the member's own
  // (see `isMemberScopeUnresolved`). The query does not retry on its own, so the
  // button is the way back.
  if (isMemberScopeUnresolved) {
    return (
      <div
        role="alert"
        className="rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900 dark:border-amber-900 dark:bg-amber-950 dark:text-amber-100"
      >
        <p>
          We could not determine which security group&apos;s policies apply to
          you, so they are not shown — the organization&apos;s defaults may not
          be the ones enforced for you.
        </p>
        <Button
          variant="outline"
          size="sm"
          className="mt-3"
          onClick={() => void refetchMyGroup()}
        >
          Try again
        </Button>
      </div>
    );
  }

  const policiesPath = `/${organizationId}/policies`;

  /*
    Sub-pages render below the scope selector's route, so they show no scope of
    their own. Name it for them: a save on the rules editor lands on whichever
    scope this tab's mutation is bound to, and copy that always said "this
    organization" would misstate a group edit. `securityGroups` is admin-only,
    so a member falls back to the group `/security-groups/me` returned.
  */
  const activeGroupName =
    securityGroups?.find(group => group.id === activeGroupId)?.name ??
    memberGroup?.name ??
    null;
  const gitleaksScopeLabel = activeGroupName ?? 'this organization';

  // KIR-3982: index → Detection, `ai-instructions` → AI Agent Instructions
  // (flag-gated), `allow-block-lists` → Allow / Block Lists; all share this
  // tab's data + mutation wiring.
  const renderPoliciesList = (section: PolicySection): JSX.Element => (
    <div className="space-y-4">
      {securityGroupsEnabled && canUseSecurityGroups ? (
        <PolicyScopeSelector
          organizationId={organizationId}
          selectedGroupId={activeGroupId}
          onScopeChange={handleScopeChange}
        />
      ) : null}
      {memberGroup ? <MemberScopeNotice groupName={memberGroup.name} /> : null}
      {requestedScopeUnavailable ? (
        <div
          role="alert"
          className="rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900 dark:border-amber-900 dark:bg-amber-950 dark:text-amber-100"
        >
          The security group you were editing is no longer available. You are
          now editing the organization&apos;s policies — changes here apply to
          everyone without a group.
        </div>
      ) : null}
      <PoliciesList
        policies={policies || []}
        loading={isLoading}
        error={errorMessage}
        canEdit={hasAccess}
        togglePolicyMutation={togglePolicyMutation}
        updateSettingsMutation={updateSettingsMutation}
        resetSettingsMutation={resetSettingsMutation}
        usePolicySettings={usePolicySettings}
        isOrganizationView={true}
        isRoleCheckLoading={isRoleCheckLoading}
        section={section}
      />
    </div>
  );

  return (
    <Routes>
      <Route index element={renderPoliciesList('detection')} />
      <Route
        path={POLICIES_AI_INSTRUCTIONS_SLUG}
        element={
          isFlagLoading ? null : catalogEnabled ? (
            renderPoliciesList('ai-instructions')
          ) : (
            <Navigate to={policiesPath} replace />
          )
        }
      />
      <Route
        path={POLICIES_ALLOW_BLOCK_SLUG}
        element={renderPoliciesList('allow-block')}
      />
      <Route
        path={AI_AGENT_INSTRUCTIONS_SLUG}
        element={
          isFlagLoading ? null : catalogEnabled ? (
            <AiInstructionCatalogPage
              organizationId={organizationId}
              canEdit={hasAccess}
              // KIR-4184: return to the AI Agent Instructions sub-page (where the
              // "Manage catalog" link lives), not the Detection Policies root.
              policiesPath={`${policiesPath}/${POLICIES_AI_INSTRUCTIONS_SLUG}`}
              backLabel="AI Agent Instructions"
              catalog={CatalogId.AgentInstructions}
            />
          ) : (
            <Navigate to={policiesPath} replace />
          )
        }
      />
      <Route
        path={EXTENSION_SECURITY_CATALOG_SLUG}
        element={
          isFlagLoading ? null : capabilityDetectionEnabled ? (
            <BuiltInCatalogPage policiesPath={policiesPath} />
          ) : (
            <Navigate to={policiesPath} replace />
          )
        }
      />
      <Route
        path={EXTENSION_SECURITY_CUSTOM_RULES_SLUG}
        element={
          isFlagLoading ? null : capabilityDetectionEnabled ? (
            <CustomCapabilityRulesPage
              usePolicySettings={usePolicySettings}
              updateSettingsMutation={updateSettingsMutation}
              canEdit={hasAccess}
              policiesPath={policiesPath}
            />
          ) : (
            <Navigate to={policiesPath} replace />
          )
        }
      />
      <Route
        path={SECRET_DETECTION_GITLEAKS_RULES_SLUG}
        element={
          isFlagLoading ? null : gitleaksRuleManagementEnabled ? (
            <GitleaksRulesPage
              usePolicySettings={usePolicySettings}
              updateSettingsMutation={updateSettingsMutation}
              canEdit={hasAccess}
              policiesPath={policiesPath}
              scopeLabel={gitleaksScopeLabel}
            />
          ) : (
            <Navigate to={policiesPath} replace />
          )
        }
      />
    </Routes>
  );
}
